import * as Sentry from '@sentry/browser'
import React, { useState, useEffect, FormEvent, KeyboardEvent } from 'react'
import { flowRight as compose, isEmpty } from 'lodash'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { parseISO } from 'date-fns'
import { RouteChildrenProps, useParams } from 'react-router-dom'

import {
  schemaForm,
  initialForm,
  formatForm,
  renderBreadcrumbsEdit,
} from 'features/Product'
import { ProductForm, WithProduct } from 'features/Product'
import { Button, PromptGuard, Modal } from 'components'
import { ModalSuccess, Confirmation } from 'components/Modal'
import { update, useFindOneById } from 'features/Product/service'
import { withBackOfficeCms } from 'helpers/hocs'
import {
  DATE_UNIT,
  ProductForForm,
  WARRANTY_TYPE,
  ProductForApiSubmit,
} from 'types'
import {
  useBreadcrumbs,
  useFormSubmitLoader,
  usePromptForm,
} from 'helpers/hooks'
import { formatUpdate } from 'features/Product/format'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { branchEditState, loaderState } from 'recoils/atoms'
import { useTranslation } from 'react-i18next'

const ProductEdit = ({
  history,
}: RouteChildrenProps<{
  branchId: string
  productId: string
}>): JSX.Element => {
  const { branchId, productId } =
    useParams<{ branchId: string; productId: string }>()
  const methods = useForm<ProductForForm>({
    defaultValues: initialForm,
    resolver: yupResolver(schemaForm),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })
  const [shouldPrompt, setShouldPrompt] = usePromptForm<ProductForForm>(methods)
  const [isSubmitting] = useFormSubmitLoader<ProductForForm>(methods)
  const [{ data: product, loading }] = useFindOneById(productId)
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false)
  const [isOpenSuccess, setIsOpenSuccess] = useState<boolean>(false)
  const [payload, setPayload] = useState<ProductForApiSubmit>()

  const setLoader = useSetRecoilState(loaderState)
  const { t } = useTranslation(['layout', 'common'])

  const base = 'f-product-form'

  useBreadcrumbs(renderBreadcrumbsEdit(branchId, productId))

  const [branchEditForm, setBranchEditForm] = useRecoilState(branchEditState)

  const callApi = async () => {
    if (!payload) return

    const updatePayload = formatUpdate(payload)
    const isSameQrCode =
      product?.qrCodeId && product.qrCodeId === updatePayload?.qrCode
    if (isSameQrCode) {
      delete updatePayload?.qrCode
    }

    if (updatePayload.qrCode === '') {
      updatePayload.qrCode = null
    }

    if (branchEditForm) {
      updatePayload.branchId = branchEditForm.id
    }

    try {
      setLoader(true)
      await update(productId, updatePayload, handleOpenSuccess)
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      setLoader(false)
    }
  }

  const handleOpenSuccess = async () => {
    setIsOpenSuccess(true)

    await setTimeout(() => {
      setShouldPrompt(false)
      handleBack()
    }, 2000)
  }

  const setData = (data: ProductForForm) => {
    const payload = formatForm(branchId, data)

    if (isEmpty(methods.errors) && payload) {
      setIsOpenConfirm(true)
      setPayload(payload)
    }
  }

  const beforeSubmit = (e: FormEvent<HTMLFormElement>) => {
    methods.setValue('currentState', 'onSubmit')
    methods.handleSubmit(setData)(e)
    methods.setValue('currentState', '')
  }

  const handleBack = (): void => {
    setBranchEditForm(undefined)
    history.push(`/branch/${branchId}/product`)
  }

  const renderConfirmModal = () => {
    return (
      isOpenConfirm && (
        <Modal
          isIcon={false}
          isOpen={isOpenConfirm}
          onClose={() => setIsOpenConfirm(false)}
        >
          <Confirmation
            title={t('common:confirmation.title.label')}
            description={t('common:confirmation.description.label')}
            confirmText={t('common:save.button')}
            cancelText={t('common:cancel.button')}
            onConfirm={callApi}
            onClose={() => setIsOpenConfirm(false)}
          />
        </Modal>
      )
    )
  }

  const renderSuccessModal = () => {
    return (
      isOpenSuccess && (
        <ModalSuccess
          isOpen={isOpenSuccess}
          description={t('common:success.label')}
          onClose={() => setIsOpenSuccess(false)}
        />
      )
    )
  }

  const checkKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') e.preventDefault()
  }

  useEffect(() => {
    setLoader(loading)
  }, [loading])

  useEffect(() => {
    if (shouldPrompt || methods.formState.isSubmitted) {
      return
    }

    if (methods.formState.isDirty) {
      setShouldPrompt(true)
    }
  }, [methods.formState])

  useEffect(() => {
    if (!product) {
      return
    }

    const warrantyPeriod = product.warrantyPeriod
      ? String(product.warrantyPeriod)
      : ''
    const warrantyUnit = product.warrantyUnit || DATE_UNIT.YEAR
    const startWarranty = product.startWarranty
      ? parseISO(product.startWarranty)
      : null
    const endWarranty = product.endWarranty
      ? parseISO(product.endWarranty)
      : null
    const maintenancePeriod = product.maintenancePeriod
      ? String(product.maintenancePeriod)
      : ''

    const nextForm: ProductForForm = {
      isActive: product.isActive,
      code: product.code,
      skuId: product.skuId,
      name: product.name,
      model: product.model,
      brand: product.brand,
      detail: product.detail,
      currency: product.saleCurrency,
      price: product.salePrice,
      serialNumber: product.serialNumber || null,
      dateOfPurchase: parseISO(product.dateOfPurchase),
      // warranty section
      warrantyType: product.warrantyType.toLowerCase() as WARRANTY_TYPE,
      warrantyPeriod: warrantyPeriod,
      warrantyUnit: warrantyUnit,
      startWarranty: startWarranty,
      endWarranty: endWarranty,
      maintenancePeriod: maintenancePeriod,
      maintenanceUnit: product.maintenanceUnit,
      qrCode: product.qrCodeId || '',
      imageProduct: product.photos ? product.photos[0] : '',
    }

    methods.reset({
      ...initialForm,
      ...nextForm,
    })
  }, [product])

  return (
    <FormProvider {...methods}>
      <PromptGuard when={shouldPrompt} />
      <form
        className={base}
        onSubmit={beforeSubmit}
        onKeyDown={(e) => checkKeyDown(e)}
      >
        <ProductForm />
        <div className={`${base}__con-bottom`}>
          <Button variants='secondary' onClick={handleBack}>
            {t('common:cancel.button')}
          </Button>
          <Button type='submit' isLoading={isSubmitting}>
            {t('common:save.button')}
          </Button>
        </div>
      </form>

      {renderConfirmModal()}
      {renderSuccessModal()}
    </FormProvider>
  )
}

export default compose(withBackOfficeCms, WithProduct)(ProductEdit)
